import { createContext, useContext } from "react";
import {
    PlaidConnection,
    PlaidConnectionWithAccounts,
} from "@common/types/plaidConnection";
import {
    FinancialConnection,
    FinancialConnectionWithAccounts,
} from "@common/types/financialConnection";
import { InvalidPlaidAccount } from "@common/dto/plaid/plaid-common";
import { FinancialAccount } from "@common/types/financialAccount";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { noop } from "@common/helpers/function";

interface TConnectAccountState {
    connection?: PlaidConnection | FinancialConnection;
    integrationConnection?: IntegrationConnection;
    connectionSaved?: boolean;
    isConnecting: boolean;
    foreign?: InvalidPlaidAccount[];
    /**
     * When set, the post-connect "Personal or Business" classification step is
     * skipped (e.g. the org integrations flow, where accounts are assigned to
     * entities directly in the table instead).
     */
    skipAccountTypeConfirmation?: boolean;
}

interface NotConnectingAccount extends Omit<
    TConnectAccountState,
    "connectionSaved" | "foreign"
> {
    isConnecting: false;
}

interface SavingConnection extends Omit<
    TConnectAccountState,
    "connectionSaved" | "foreign"
> {
    isConnecting: true;
}

interface ConnectionSaved extends TConnectAccountState {
    connection: PlaidConnectionWithAccounts | FinancialConnectionWithAccounts;
    connectionSaved: boolean;
    isConnecting: false;
    foreign?: InvalidPlaidAccount[];
}

interface IntegrationConnectionSaved extends TConnectAccountState {
    integrationConnection: IntegrationConnection;
    connectionSaved: boolean;
    isConnecting: false;
    foreign?: InvalidPlaidAccount[];
}

export type ConnectAccountState =
    | NotConnectingAccount
    | SavingConnection
    | ConnectionSaved
    | IntegrationConnectionSaved;

interface ConnectAccountContextValue {
    state: ConnectAccountState;
    updateConnectAccountState(newState: ConnectAccountState): void;
    updateFinancialAccount(account: FinancialAccount): void;
}

export const ConnectAccountContext = createContext<ConnectAccountContextValue>({
    state: {
        isConnecting: false,
    },
    updateConnectAccountState: noop,
    updateFinancialAccount: noop,
});

export function useConnectAccount() {
    const context = useContext(ConnectAccountContext);

    if (!context) {
        throw new Error(
            "useConnectAccount must be used within a ConnectAccountProvider",
        );
    }

    return context;
}
